import {
  ActionType,
  EmptyListingButtonTypes,
  ExtraFilter,
  FilterAll,
  FilterComponent,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  ListingSelectOption,
  Operator,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useNavigation,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  ADBS_CREATE_ROUTE,
  APEX_CREATE_ROUTE,
  DetailsPanelId,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  GroupingKeys,
  ListingTestIds,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
  ViewKeys,
} from "../../constants/uiConstants";
import { AzureSubscriptionSummary } from "../../gen/clients/mchub-azure-api-client";
import {
  AdbsDatabaseCollection,
  AdbsDatabaseSummary,
  AdbsDatabaseSummaryStatusEnum,
} from "../../gen/clients/mchub-azure-api-client-adb";
import { parseId } from "../../helpers/idHelper";
import {
  AdbWorkloadType,
  getFieldCustomOptions,
  getStatusInfo,
  getWorkloadTypeDisplayName,
  optimizedRetryOption,
  responseItemstoArray,
  statusSortComparator,
  workloadTypeSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFeatures } from "../../hooks/useFeatures";
import { useFilterStatePills } from "../../hooks/useFilterStatePills";
import { useLocationFilters } from "../../hooks/useLocationFilters";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { AdbsDeleteProps, newAdbsDelete } from "../../operations/Adbs/AdbsDelete";
import { chainFunctions, getAzureLocationName, getOciRegion } from "../../utils";

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.ADBS_DETAILS].map(config => config.key);

export interface AdbsItem {
  id: string;
  displayName: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceGroupName: string;
  location: string;
  dbName: string;
  dbWorkload: AdbWorkloadType;
  status: AdbsDatabaseSummaryStatusEnum;
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
  freeformTags?: { [key: string]: string; };
}

export enum ColumnIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  WorkloadType = "dbWorkload",
  FreeformTags = "freeformTags",
}

export enum ColumnTestIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  WorkloadType = "dbWorkload",
  FreeformTags = "freeformTags",
}

export enum ActionIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

export enum ActionBarTestIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

export enum FilterTestIds {
  SubscriptionFilter = "subscription-filter",
  LocationFilter = "location-filter"
}

interface AzureSubscriptionSummaryMap {
  [key: string]: AzureSubscriptionSummary;
}

export interface AdbsListProps {
  isApex?: boolean;
}

export const AdbsList = ({ isApex }: AdbsListProps): JSX.Element => {
  const { enableApex } = useFeatures();

  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { navigateTo } = useNavigation(ConsoleContext);
  // eslint-disable-next-line max-len
  const { preferredSubscription, locale } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);
  const { listId } = useParams();

  const [selection, setSelection] = React.useState<AdbsItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] = React.useState<string>("");
  const [subscriptionOptions, setSubscriptionOptions] = React.useState<{ key: string, text: string }[]>([]);
  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();

  const { locationOptions, selectedLocation, setSelectedLocation } = useLocationFilters(
    filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
  );

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const [filterComponentRef, setFilterComponentRef] = React.useState<FilterComponent>();
  const pillFilterValues = filterComponentRef?.getFilters();
  const filterTextValue = filterComponentRef?.getFilterText();
  const [filterText, setFilterText] = React.useState(filterState.filterTextValue || filterTextValue);

  const { perPageItemsCount } = useRowCount();

  const getLocation = (value: AdbsItem): JSX.Element => (
    <span>{getAzureLocationName(value.location)}</span>
  );

  const getStatus = (value: AdbsItem): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status,
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
        )
      }
    />
  );

  const getWorkloadType = (value: AdbsItem): JSX.Element => (
    <span>{getWorkloadTypeDisplayName(value.dbWorkload)}</span>
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.DisplayName,
      testId: ColumnTestIds.DisplayName,
      name: Messages.common.name(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.displayName}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.ADBS_DETAILS][0].panelPath}?location=${value.location}${(isApex ? "&type=apex" : "")}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
          iconName={isApex ? SvgIconIds.apexSvg : SvgIconIds.adbSvg}
        />
      ),
    },
    {
      itemProp: ColumnIds.SubscriptionName,
      testId: ColumnTestIds.SubscriptionName,
      name: Messages.labels.subscription(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureSubscriptionLink resourceId={value.id} subscriptionName={value.subscriptionName} />,
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: AdbsItem,
        b: AdbsItem,
      ) => statusSortComparator<AdbsItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.WorkloadType,
      testId: ColumnTestIds.WorkloadType,
      name: Messages.labels.workloadType(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: AdbsItem,
        b: AdbsItem,
      ) => workloadTypeSortComparator<AdbsItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getWorkloadType(value),
    },
  ];

  const { refresh: adbsRefresh, loading: adbsLoading, response: adbsResponse } = useQueryCall({
    wait: !selectedSubscription || !selectedLocation,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).adbsDatabaseApi.listAdbsDatabasesBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadAdbs(),
      },
    },
  });

  const adbs = adbsResponse
    && responseItemstoArray<AdbsDatabaseCollection, AdbsDatabaseSummary>(adbsResponse);

  const groupingOptions: ListingSelectOption[] = [
    {
      key: GroupingKeys.NoGrouping,
      text: Messages.listAdbs.filters.noGrouping(),
    },
  ];

  const viewOptions: ViewSelectOption[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listAdbs.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const adbsSummaryToAdbsItem = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: any[],
    subscriptionsMap: AzureSubscriptionSummaryMap,
  ): AdbsItem[] => {
    const adbsItems: AdbsItem[] = [];
    items.forEach((item: AdbsDatabaseSummary) => {
      const { subscriptionId, resourceGroup } = parseId(item.id);
      const itemSubscriptionName = subscriptionsMap && subscriptionsMap[subscriptionId].name;

      if (!enableApex || (isApex && item.dbWorkload === AdbWorkloadType.APEX)
        || (!isApex && item.dbWorkload !== AdbWorkloadType.APEX)) {
        adbsItems.push({
          id: item.id,
          displayName: item.name,
          subscriptionId,
          subscriptionName: itemSubscriptionName,
          resourceGroupName: resourceGroup,
          location: item.location,
          dbName: item.dbName,
          dbWorkload: item.dbWorkload as AdbWorkloadType,
          status: item.status,
          freeformTags: item.freeformTags,
        });
      }
    });
    return adbsItems;
  };

  const items = React.useMemo(() => {
    let newInstance: AdbsItem[] = [];

    if (adbs && subscriptions && subscriptions.length > 0) {
      const subscriptionsMap = subscriptions.reduce((
        map: AzureSubscriptionSummaryMap,
        subscription: AzureSubscriptionSummary,
      ) => {
        map[subscription.id] = subscription;
        return map;
      }, {});
      newInstance = adbsSummaryToAdbsItem(adbs, subscriptionsMap);
    }
    return newInstance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(adbs), JSON.stringify(subscriptions)]);

  React.useEffect(() => {
    const newSubscriptionOptions: { key: string, text: string }[] = [];
    // eslint-disable-next-line no-unused-expressions
    subscriptions && subscriptions.forEach(subscription => {
      newSubscriptionOptions.push({ key: subscription.id, text: subscription.name });
    });

    setSubscriptionOptions(newSubscriptionOptions);
    if (filterState.defaultSubscription) {
      setSelectedSubscription(filterState.defaultSubscription);
      if (defaultExtraFilters) defaultExtraFilters[0].value = filterState.defaultSubscription;
    } else if (preferredSubscription) {
      setSelectedSubscription(preferredSubscription);
    } else {
      setSelectedSubscription(newSubscriptionOptions.length ? newSubscriptionOptions[0].key : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptions)]);

  const subscriptionFilter: ExtraFilter = {
    testId: FilterTestIds.SubscriptionFilter,
    name: columns[1].name,
    operator: Operator.Equal,
    value: filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
    fieldKey: columns[1].itemProp,
    customOptions: subscriptionOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedSubscription(value);
        filterState.setDefaultSubscription(value);
      }
    },
    hideDelete: true,
  };

  const resourceGroupFilter: ExtraFilter = {
    name: columns[2].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[2].itemProp)?.value || [FilterAll],
    fieldKey: columns[2].itemProp,
    customOptions: getFieldCustomOptions(items?.map(item => item.resourceGroupName), (fieldVal: string) => fieldVal),
  };

  React.useEffect(() => {
    if (filterState.defaultLocation) {
      setSelectedLocation(filterState.defaultLocation);
      if (defaultExtraFilters) defaultExtraFilters[1].value = filterState.defaultLocation;
    } else {
      setSelectedLocation(filterState.pillFilterStateValue.filterValues?.[1]?.value || selectedLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationFilter: ExtraFilter = {
    testId: FilterTestIds.LocationFilter,
    name: columns[3].name,
    operator: Operator.Equal,
    value: filterState.defaultLocation ? filterState.defaultLocation : selectedLocation,
    fieldKey: columns[3].itemProp,
    customOptions: locationOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedLocation(value);
        filterState.setDefaultLocation(value);
      }
    },
    hideDelete: true,
  };

  const lifecycleStateFilter: ExtraFilter = {
    name: columns[4].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[4].itemProp)?.value || [FilterAll],
    fieldKey: columns[4].itemProp,
    messageCallBack: (value: string) => stateT(value),
    customOptions: getFieldCustomOptions(items?.map(item => item.status), (fieldVal: string) => stateT(fieldVal)),
  };

  const allFilterPills = [
    subscriptionFilter,
    locationFilter,
    resourceGroupFilter,
    lifecycleStateFilter,
  ];

  const filterPills = [
    subscriptionFilter,
    locationFilter,
  ];

  const { trigger: triggerDeleteAdbs } = useOperation<AdbsDeleteProps>(newAdbsDelete);
  const { closePanels } = useSidePanel();

  const adbsActions: ActionType[] = [
    {
      key: ActionIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionIds.Create, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
        const route = isApex ? APEX_CREATE_ROUTE : ADBS_CREATE_ROUTE;
        navigateTo(route, PageId.ADBS_CREATE, {
          analytics: {
            pageId: PageId.ADB_LIST_HUB,
            panelId: DetailsPanelId.ADBS_DATABASES,
            actionName: ActionIds.Create,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
        chainFunctions(adbsRefresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: !selection.length,
      title: Messages.labels.deleteAdbs(),
      description: Messages.hints.deleteAdbsConfirmation(selection[0] ? selection[0].displayName : ""),
      onClick: () => trackActionClick(ActionIds.Delete, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
      onConfirm: () => {
        triggerDeleteAdbs({
          databaseId: selection[0].id,
          location: selectedLocation,
          onExecute: () => {
            chainFunctions(adbsRefresh, listingComponentRef?.resetTableSelection);
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  const textFilterCallBack = (rowItem: AdbsItem): AdbsItem => {
    const displayRowItem = { ...rowItem };
    displayRowItem.status = stateT(
      displayRowItem.status,
    ) as AdbsDatabaseSummaryStatusEnum;
    displayRowItem.location = getAzureLocationName(displayRowItem.location);
    return displayRowItem;
  };

  React.useEffect(() => {
    setFilterText(filterTextValue);
    filterState.setFilterTextValue(filterTextValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTextValue]);

  const defaultExtraFilters = useFilterStatePills(pillFilterValues, allFilterPills, listId, filterPills);

  const resetFilterValues = (): void => {
    const defaultPillValues = filterPills.map(filter => ({ fieldKey: filter.fieldKey, value: filter.value as string }));
    filterState.setFilterStateValue({ filterValues: defaultPillValues, listId, defaultPills: filterPills });
    filterState.setFilterTextValue("");
    setFilterText("");
  };

  return (
    <Listing
      testId={ListingTestIds.Adbs}
      items={items}
      listColumns={columns}
      listingComponentRef={setListingComponentRef}
      itemKey={ColumnIds.DisplayName}
      itemsPerPage={perPageItemsCount}
      actionBarItems={adbsActions}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      emptyList={{
        title: isApex ? Messages.listApex.emptyList.title() : Messages.listAdbs.emptyList.title(),
        emptyButtons: [
          {
            type: EmptyListingButtonTypes.PRIMARY,
            text: isApex ? Messages.actions.createApex() : Messages.actions.createAdbs(),
            action: () => {
              trackActionClick(ActionIds.Create, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
              const route = isApex ? APEX_CREATE_ROUTE : ADBS_CREATE_ROUTE;
              navigateTo(route, PageId.ADBS_CREATE);
            },
          },
        ],
        watermark: isApex ? SvgIconIds.apexWatermarkSvg : SvgIconIds.adbWatermarkSvg,
      }}
      isLoading={adbsLoading || subscriptionLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.DisplayName,
      }}
      groupingSelect={{
        groupingOptions,
        defaultSelectedKey: GroupingKeys.NoGrouping,
      }}
      viewSelect={{
        viewOptions,
        defaultSelectedKey: ViewKeys.ListView,
      }}
      filtering={{
        allExtraFilters: allFilterPills,
        defaultExtraFilters,
        defaultFilterText: filterText,
        textFilterCallBack,
        componentRef: setFilterComponentRef,
        onClearFilters: resetFilterValues,
      }}
    />
  );
};
